import { motion } from "framer-motion";
import React, { memo, useCallback, useMemo, useState } from "react";
import { IconContext } from "react-icons";
import * as MdIcons from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import Icon, { IconNames } from "../../core/components/Icon.tsx";
import Separator from "../../core/components/Separator.tsx";
import useIsMobile from "../../hooks/useIsMobile.ts";
import useOutsideClick from "../../hooks/useOutsideClick.ts";
import iconeUsuario from "../../images/iconeUsuario.png";
import logoNavbar from "../../images/logoNavbar.png";
import ModalAlerta from "../ModalAlerta/ModalAlerta.tsx";
import ModalConfirmacao2 from "../ModalConfirmacao2/ModalConfirmacao2.tsx";
import ModalNotificacao from "../ModalNotificacao/ModalNotificacao.tsx";
import "../NavbarV3/NavbarV3.css";
import MobileNavbar from "./Components/MobileNavbar.tsx";
import {
	NavbarItems,
	NavbarItemsMobile,
	NavbarRightItems,
} from "./Dropdowns/constants/Dropdown.constants.tsx";
import DropdownPesquisa from "./Dropdowns/DropdownPesquisa.tsx";
import DropdownUser from "./Dropdowns/DropdownUser.tsx";
import { NavbarRightItemsEnum } from "./Dropdowns/interfaces/Dropdown.enum.ts";

interface NavbarProps {
	title: string;
	notificoes?: string;
}

interface NavbarRightItemProps {
	icon?: IconNames;
	img?: string;
	children: React.ReactNode;
}

interface NavbarItemProps {
	title: string;
	children: React.ReactNode;
	inlineExpand?: boolean;
	icon?: IconNames;
}

const NavbarItem = ({ title, children, inlineExpand }: NavbarItemProps) => {
	const [open, setOpen] = useState(false);
	const [isOpenDropdown, toggleDropdown, , refDropdown] = useOutsideClick();
	if (inlineExpand) {
		return (
			<div className="navbar-item-mobile">
				<div className="item-mobile" onClick={() => setOpen((v) => !v)}>
					<p className="items-menu-mobile">{title}</p>
					<MdIcons.MdOutlineKeyboardArrowDown
						style={{
							transform: open ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.2s",
						}}
						color="#FFF"
					/>
				</div>
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={
						open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }
					}
					transition={{ duration: 0.18 }}
					style={{ overflow: "hidden" }}
				>
					{children}
				</motion.div>
			</div>
		);
	}

	return (
		<div ref={refDropdown} className="navbar-title" onClick={toggleDropdown}>
			<p className="title-menu">{title}</p>
			{isOpenDropdown ? (
				<>
					<MdIcons.MdOutlineKeyboardArrowDown color="#FFF" />
					{children}
				</>
			) : (
				<MdIcons.MdOutlineKeyboardArrowUp color="#FFF" />
			)}
		</div>
	);
};

const MobileNavbarItem = ({ title, children, icon }: NavbarItemProps) => {
	const [open, setOpen] = useState(false);

	return (
		<div className="mobile-list-item">
			<div className="item-mobile" onClick={() => setOpen((v) => !v)}>
				<div className="mobile-list-icon">
					{icon && <Icon name={icon} size="xl" theme="darkGrey" />}
					<p className="items-menu-mobile">{title}</p>
				</div>
				<MdIcons.MdOutlineKeyboardArrowDown
					style={{
						transform: open ? "rotate(180deg)" : "rotate(0deg)",
						transition: "transform 0.2s",
					}}
					color="#FFF"
				/>
			</div>

			<motion.div
				initial={{ height: 0, opacity: 0 }}
				animate={
					open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }
				}
				transition={{ duration: 0.18 }}
				style={{ overflow: "hidden", width: "100%" }}
			>
				{children}
			</motion.div>
		</div>
	);
};

const NavbarRightItem = ({ icon, img, children }: NavbarRightItemProps) => {
	const [isOpenDropdown, toggleDropdown, , refDropdown] = useOutsideClick();

	if (!icon && !img) return;

	const className = img ? "image-perfil" : "navbar-title";
	return (
		<div ref={refDropdown} className={className} onClick={toggleDropdown}>
			{icon && !img && <Icon name={icon} size="xl" theme="light" />}
			{img && <img src={img} alt="icone" />}
			{isOpenDropdown && children}
		</div>
	);
};

const PageTitle = ({ title }: { title: string }) => {
	return (
		<div className="page-title">
			<p className="title">{title}</p>
		</div>
	);
};

export const NavbarItemList = () => {
	const navigate = useNavigate();

	const isMobile = useIsMobile();

	const navbarItemsToUse = useMemo(() => {
		if (isMobile) {
			return NavbarItemsMobile;
		}
		return NavbarItems;
	}, [isMobile]);

	const filteredItems = useMemo(() => {
		if (isMobile) {
			return Object.keys(NavbarItemsMobile);
		}
		return Object.keys(NavbarItems);
	}, [isMobile]);

	const isMobileSidebar = useIsMobile(768);

	if (isMobileSidebar) {
		return (
			<div className="navbar-items-mobile">
				{filteredItems.map((key) => {
					const item = navbarItemsToUse[key];
					return (
						<MobileNavbarItem
							key={key}
							title={item.titleNavbar || ""}
							icon={item.icon}
						>
							{item.dropdown()}
						</MobileNavbarItem>
					);
				})}
			</div>
		);
	}

	return (
		<div className="navbar-icons">
			<div className="navbar-icons-container">
				<div
					onClick={() => {
						navigate("/");
					}}
					className="navbar-inicio"
				>
					<img
						style={{ cursor: "pointer" }}
						src={logoNavbar}
						alt="logoNavbar"
						className="iconeMenu"
					/>
					<p className="title-menu-inicio">Início</p>
				</div>
				<Separator direction="horizontal" size="sm" />
				<DropdownPesquisa />
			</div>

			<Separator direction="horizontal" size="sm" />

			{filteredItems.map((key) => {
				const item = navbarItemsToUse[key];
				return (
					<NavbarItem
						key={key}
						title={item.titleNavbar || ""}
						children={item.dropdown()}
					/>
				);
			})}

			<Separator direction="horizontal" size="sm" />
		</div>
	);
};

function NavbarV3({ notificoes, title }: NavbarProps) {
	const { logout, coresEmpresa, profile } = useProfile();

	const navigate = useNavigate();
	const [isOpenLogoutModal, toggleLogoutModal, closeLogoutModal, ,] =
		useOutsideClick();
	const [
		isOpenNotificacao,
		toggleNotificacao,
		closeNotificacao,
		refNotificacao,
	] = useOutsideClick();
	const [isOpenExpiracao, , closeExpiracao] = useOutsideClick();
	const [
		isOpenDropdownUser,
		toggleUserDropdownOpen,
		closeUserDropdownOpen,
		refUser,
	] = useOutsideClick();

	const isMobileSidebar = useIsMobile(768);

	const handleClose = useCallback(() => {
		closeLogoutModal();
	}, [closeLogoutModal]);

	const handleCloseExpiracao = useCallback(() => {
		closeExpiracao();
	}, [closeExpiracao]);

	const handleCloseNotificacao = useCallback(() => {
		closeNotificacao();
	}, [closeNotificacao]);

	const handleLogoutClick = useCallback(() => {
		closeUserDropdownOpen();
		toggleLogoutModal();
	}, [closeUserDropdownOpen, toggleLogoutModal]);

	if (isMobileSidebar) {
		return <MobileNavbar title={title} handleLogoutClick={logout} />;
	}

	return (
		<>
			<IconContext.Provider value={{ color: "#fff" }}>
				<div
					className="navbar"
					style={{ backgroundColor: coresEmpresa.headerFooter }}
				>
					<NavbarItemList />

					<PageTitle title={title} />
					<div className="right-icons">
						<div
							onClick={() => {
								navigate("/ajuda");
							}}
							className="navbar-title"
						>
							<Icon name="MessageCircleQuestion" size="xl" theme="light" />
						</div>
						<div
							ref={refNotificacao}
							onClick={toggleNotificacao}
							className="navbar-title"
						>
							<Icon name="Bell" size="xl" theme="light" />
						</div>
						<NavbarRightItem
							icon={NavbarRightItems[NavbarRightItemsEnum.CONFIG].icon}
							children={NavbarRightItems[
								NavbarRightItemsEnum.CONFIG
							].dropdown()}
						/>
						<div
							ref={refUser}
							onClick={toggleUserDropdownOpen}
							className="image-perfil"
						>
							{profile?.profileImage && (
								<img
									src={
										profile?.profileImage && profile?.profileImage.tipo
											? `data:${profile?.profileImage.tipo};base64,${profile?.profileImage.dado}`
											: iconeUsuario
									}
									alt="Uploaded preview"
								/>
							)}
							{!profile?.profileImage && (
								<Icon name="User" size="xl" theme="light" />
							)}
						</div>
						{isOpenDropdownUser && (
							<DropdownUser
								ref={refUser}
								handleLogoutClick={handleLogoutClick}
							/>
						)}
					</div>
				</div>
			</IconContext.Provider>
			<ModalConfirmacao2
				titulo="Sair"
				texto="Deseja Sair do Sistema?"
				tituloBotao="Cancelar"
				textoBotao="Sair"
				open={isOpenLogoutModal}
				onClick={logout}
				onClose={handleClose}
			/>
			<ModalNotificacao
				titulo={notificoes}
				tituloConfirmar="OK"
				open={isOpenNotificacao}
				onClose={handleCloseNotificacao}
			/>
			<ModalAlerta
				open={isOpenExpiracao}
				onClose={handleCloseExpiracao}
				titulo="Expiração"
			/>
		</>
	);
}

export default memo(NavbarV3);
